import { normalizeCellSpans } from '../spreadsheet/cell-span.ts';
import { normalizeFloatingImages } from '../spreadsheet/floating-image.ts';
import { sanitizeCellStyle } from '../spreadsheet/cell-style.ts';
import { cellKey, type InMemoryDataInitValue } from '../spreadsheet/data-store.ts';
import { sanitizePhosphorIconName } from '../spreadsheet/cell-display.ts';
import type {
  SheetCellOverflowMode,
  SpreadsheetCellStyleDeclarations,
  SpreadsheetColumn,
  SpreadsheetConfig,
  SpreadsheetSelectOption,
  UiToolbarCapability,
} from '../spreadsheet/types.ts';
import { normalizePartnerChromeActions } from './chrome-actions.ts';
import type { PartnerSheetPayload } from './types.ts';

function normalizeCellOverflow(value: unknown): SheetCellOverflowMode | undefined {
  if (typeof value !== 'string') return undefined;
  const mode = value.trim().toLowerCase();
  return mode === 'clip' || mode === 'spill' ? mode : undefined;
}

function normalizeSelectOption(x: unknown): SpreadsheetSelectOption | null {
  if (!x || typeof x !== 'object') return null;
  const o = x as Record<string, unknown>;
  if (typeof o.value !== 'string') return null;
  const out: SpreadsheetSelectOption = { value: o.value };
  if (typeof o.label === 'string') out.label = o.label;
  if (typeof o.icon === 'string') out.icon = o.icon;
  if (typeof o.color === 'string') out.color = o.color;
  if (typeof o.backgroundColor === 'string') out.backgroundColor = o.backgroundColor;
  return out;
}

function normalizeColumn(c: unknown): SpreadsheetColumn | null {
  if (!c || typeof c !== 'object') return null;
  const o = c as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.header !== 'string') return null;
  const widthPx = typeof o.widthPx === 'number' && Number.isFinite(o.widthPx) && o.widthPx > 0 ? o.widthPx : 120;
  const col: SpreadsheetColumn = { id: o.id, header: o.header, widthPx };
  if (typeof o.readOnly === 'boolean') col.readOnly = o.readOnly;
  if (
    o.valueType === 'text' ||
    o.valueType === 'number' ||
    o.valueType === 'select' ||
    o.valueType === 'link'
  ) {
    col.valueType = o.valueType;
  }
  if (col.valueType === 'link') {
    if (typeof o.icon === 'string') {
      const icon = sanitizePhosphorIconName(o.icon);
      if (icon) col.icon = icon;
    }
    if (typeof o.openInNewTab === 'boolean') col.openInNewTab = o.openInNewTab;
    if (typeof o.label === 'string') {
      const label = o.label.trim().slice(0, 500);
      if (label) col.label = label;
    }
  }
  if (Array.isArray(o.selectOptions)) {
    const opts: SpreadsheetSelectOption[] = [];
    for (const x of o.selectOptions) {
      const so = normalizeSelectOption(x);
      if (so) opts.push(so);
    }
    if (opts.length) col.selectOptions = opts;
  }
  if (typeof o.allowEmpty === 'boolean') col.allowEmpty = o.allowEmpty;
  if (typeof o.hidden === 'boolean') col.hidden = o.hidden;
  if (col.hidden === true) col.readOnly = true;
  return col;
}

/**
 * Flatten partner JSON into a grid payload.
 * Supports either a flat `{ columns, rows, ... }` or a nested `{ title, sheets: [{ columns, rows, ... }] }`
 * where the outer `title` is typically the page / view title.
 */
export function normalizePartnerSheetPayload(raw: unknown): PartnerSheetPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  let src: Record<string, unknown> = o;

  if (Array.isArray(o.sheets) && o.sheets.length > 0) {
    const inner = o.sheets[0];
    if (!inner || typeof inner !== 'object') return null;
    const i = inner as Record<string, unknown>;
    if (!Array.isArray(i.columns) || !Array.isArray(i.rows)) return null;
    src = {
      ...i,
      title: typeof o.title === 'string' ? o.title : typeof i.title === 'string' ? i.title : undefined,
      description:
        typeof o.description === 'string'
          ? o.description
          : typeof i.description === 'string'
            ? i.description
            : undefined,
      rowCount:
        typeof i.rowCount === 'number' && i.rowCount >= 1
          ? i.rowCount
          : typeof o.rowCount === 'number' && o.rowCount >= 1
            ? o.rowCount
            : undefined,
      ghostRowCount:
        typeof i.ghostRowCount === 'number' && i.ghostRowCount >= 0 && Number.isFinite(i.ghostRowCount)
          ? Math.floor(i.ghostRowCount)
          : typeof o.ghostRowCount === 'number' && o.ghostRowCount >= 0 && Number.isFinite(o.ghostRowCount)
            ? Math.floor(o.ghostRowCount)
            : undefined,
      defaultRowHeightPx:
        typeof i.defaultRowHeightPx === 'number' && i.defaultRowHeightPx > 0
          ? i.defaultRowHeightPx
          : typeof o.defaultRowHeightPx === 'number' && o.defaultRowHeightPx > 0
            ? o.defaultRowHeightPx
            : undefined,
      cellOverflow: i.cellOverflow ?? o.cellOverflow,
      enabledUiCapabilities: i.enabledUiCapabilities ?? o.enabledUiCapabilities,
      chromeActions: i.chromeActions ?? o.chromeActions,
      cells: i.cells ?? o.cells,
      cellStyles: i.cellStyles ?? o.cellStyles,
      cellSpans: i.cellSpans ?? o.cellSpans,
      floatingImages: i.floatingImages ?? o.floatingImages,
      pdfExport: i.pdfExport ?? o.pdfExport,
    };
  }

  if (!Array.isArray(src.columns) || !Array.isArray(src.rows)) return null;
  const columns = (src.columns as unknown[]).map(normalizeColumn).filter((c): c is SpreadsheetColumn => c !== null);
  if (columns.length === 0) return null;
  const rows = (src.rows as unknown[]).filter((r): r is Record<string, unknown> => r !== null && typeof r === 'object');
  const chromeActions = normalizePartnerChromeActions(src.chromeActions);
  const sparseCells = normalizeSparseCells(src.cells);
  const cellStyles = normalizeCellStyles(src.cellStyles);
  const rowCount = typeof src.rowCount === 'number' && src.rowCount >= 1 ? src.rowCount : undefined;
  const cellSpans = normalizeCellSpans(src.cellSpans, {
    maxRow: partnerEffectiveRowCount({ columns, rows, rowCount }),
    maxCol: columns.length,
  });
  const floatingImages = normalizeFloatingImages(src.floatingImages).filter((image) => image.src);
  return {
    ...(sparseCells !== undefined ? { cells: sparseCells } : {}),
    ...(cellStyles !== undefined ? { cellStyles } : {}),
    ...(cellSpans.length > 0 ? { cellSpans } : {}),
    ...(floatingImages.length > 0 ? { floatingImages } : {}),
    ...(typeof src.revision === 'number' && Number.isFinite(src.revision)
      ? { revision: src.revision }
      : {}),
    title: typeof src.title === 'string' ? src.title : undefined,
    description: typeof src.description === 'string' ? src.description : undefined,
    columns,
    rows,
    rowCount: typeof src.rowCount === 'number' && src.rowCount >= 1 ? src.rowCount : undefined,
    ghostRowCount:
      typeof src.ghostRowCount === 'number' && src.ghostRowCount >= 0 && Number.isFinite(src.ghostRowCount)
        ? Math.floor(src.ghostRowCount)
        : undefined,
    defaultRowHeightPx:
      typeof src.defaultRowHeightPx === 'number' && src.defaultRowHeightPx > 0
        ? src.defaultRowHeightPx
        : undefined,
    ...(normalizeCellOverflow(src.cellOverflow) !== undefined
      ? { cellOverflow: normalizeCellOverflow(src.cellOverflow) }
      : {}),
    enabledUiCapabilities: Array.isArray(src.enabledUiCapabilities)
      ? (src.enabledUiCapabilities.filter((s): s is UiToolbarCapability => typeof s === 'string') as UiToolbarCapability[])
      : undefined,
    ...(chromeActions !== undefined ? { chromeActions } : {}),
    ...(src.pdfExport === false ? { pdfExport: false } : {}),
  };
}

const SPARSE_CELL_KEY_RE = /^[1-9]\d*:[1-9]\d*$/;

/** Validate a server sparse cells map (`"row:col"` keys, scalar values). */
function normalizeSparseCells(raw: unknown): Record<string, string | number> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, string | number> = {};
  let any = false;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!SPARSE_CELL_KEY_RE.test(k)) continue;
    if (typeof v === 'string' || (typeof v === 'number' && Number.isFinite(v))) {
      out[k] = v;
      any = true;
    }
  }
  return any ? out : undefined;
}

/** Validate a `"row:col"` style map. Unsafe declarations are dropped. */
function normalizeCellStyles(
  raw: unknown,
): Record<string, SpreadsheetCellStyleDeclarations> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, SpreadsheetCellStyleDeclarations> = {};
  let any = false;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!SPARSE_CELL_KEY_RE.test(k)) continue;
    const style = sanitizeCellStyle(v);
    if (!style) continue;
    out[k] = style;
    any = true;
  }
  return any ? out : undefined;
}

function cellValueFromRow(row: Record<string, unknown>, columnId: string): string | number | undefined {
  const v = row[columnId];
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'string') return v;
  return String(v);
}

/**
 * Map API rows (objects keyed by column id) to createInMemoryDataStore initial keys `"row:col"`.
 * `sparseCells` (out-of-schema cells keyed `"row:col"`) are merged in when supplied.
 */
export function rowsToInitialMap(
  columns: readonly SpreadsheetColumn[],
  rows: ReadonlyArray<Record<string, unknown>>,
  sparseCells?: Record<string, string | number>,
  cellStyles?: Record<string, SpreadsheetCellStyleDeclarations>,
): Record<string, InMemoryDataInitValue> {
  const initial: Record<string, InMemoryDataInitValue> = {};
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r]!;
    const rowIndex = r + 1;
    for (let c = 0; c < columns.length; c++) {
      const col = columns[c]!;
      const v = cellValueFromRow(row, col.id);
      if (v !== undefined) {
        initial[cellKey(rowIndex, c + 1)] = v;
      }
    }
  }
  if (sparseCells) {
    for (const [k, v] of Object.entries(sparseCells)) {
      initial[k] = v;
    }
  }
  if (cellStyles) {
    for (const [k, raw] of Object.entries(cellStyles)) {
      const style = sanitizeCellStyle(raw);
      if (!style) continue;
      const existing = initial[k];
      if (existing === undefined) {
        initial[k] = { value: '', style };
      } else if (typeof existing === 'object') {
        initial[k] = { ...existing, style };
      } else {
        initial[k] = { value: existing, style };
      }
    }
  }
  return initial;
}

/** Build SpreadsheetConfig from a validated payload and an existing data store. */
export function sheetPayloadToConfig(
  payload: PartnerSheetPayload,
  data: SpreadsheetConfig['data'],
): SpreadsheetConfig {
  const { columns, rows, rowCount, ghostRowCount, defaultRowHeightPx, enabledUiCapabilities } = payload;
  const rc = partnerEffectiveRowCount(payload);
  const gc =
    typeof ghostRowCount === 'number' && ghostRowCount >= 0 && Number.isFinite(ghostRowCount)
      ? Math.min(Math.floor(ghostRowCount), 10_000)
      : undefined;

  return {
    columns,
    rowCount: rc,
    ...(gc !== undefined && gc > 0 ? { ghostRowCount: gc } : {}),
    defaultRowHeightPx,
    ...(payload.cellOverflow === 'clip' ? { cellOverflow: 'clip' as const } : {}),
    data,
    enabledUiCapabilities,
  };
}

/** Resolved grid height for a partner payload (same rule as `sheetPayloadToConfig`). */
export function partnerEffectiveRowCount(payload: PartnerSheetPayload): number {
  const minRows = Math.max(payload.rows.length, 1);
  const { rowCount } = payload;
  return rowCount !== undefined && rowCount >= minRows ? rowCount : Math.max(minRows, 100);
}

export type SheetTruthNormalized = { sheetPath: string; payload: PartnerSheetPayload };

/**
 * Validate a BFF-broadcast `sheet.truth` body (partner push).
 * When `columns` is omitted, `fallbackColumns` from the last routing load must be supplied.
 */
export function normalizeSheetTruthPayload(
  raw: unknown,
  fallbackColumns: readonly SpreadsheetColumn[] | null,
): SheetTruthNormalized | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.type !== 'sheet.truth') return null;
  const sheetPath = typeof o.sheetPath === 'string' && o.sheetPath.trim() ? o.sheetPath.trim() : null;
  if (!sheetPath) return null;
  if (!Array.isArray(o.rows)) return null;
  const rows = (o.rows as unknown[]).filter((r): r is Record<string, unknown> => r !== null && typeof r === 'object');
  let columns: SpreadsheetColumn[];
  if (Array.isArray(o.columns)) {
    columns = (o.columns as unknown[]).map(normalizeColumn).filter((c): c is SpreadsheetColumn => c !== null);
    if (columns.length === 0) return null;
  } else if (fallbackColumns && fallbackColumns.length > 0) {
    columns = [...fallbackColumns];
  } else {
    return null;
  }
  const rowCount = typeof o.rowCount === 'number' && o.rowCount >= 1 ? o.rowCount : undefined;
  const ghostRowCount =
    typeof o.ghostRowCount === 'number' && o.ghostRowCount >= 0 && Number.isFinite(o.ghostRowCount)
      ? Math.floor(o.ghostRowCount)
      : undefined;
  const enabledUiCapabilities = Array.isArray(o.enabledUiCapabilities)
    ? (o.enabledUiCapabilities.filter((s): s is UiToolbarCapability => typeof s === 'string') as UiToolbarCapability[])
    : undefined;
  const chromeActions = normalizePartnerChromeActions(o.chromeActions);
  const sparseCells = normalizeSparseCells(o.cells);
  const cellStyles = normalizeCellStyles(o.cellStyles);
  const cellSpans = normalizeCellSpans(o.cellSpans, {
    maxRow: partnerEffectiveRowCount({ columns, rows, rowCount }),
    maxCol: columns.length,
  });
  const floatingImages = normalizeFloatingImages(o.floatingImages).filter((image) => image.src);
  const payload: PartnerSheetPayload = {
    title: typeof o.title === 'string' ? o.title : undefined,
    description: typeof o.description === 'string' ? o.description : undefined,
    columns,
    rows,
    rowCount,
    ...(ghostRowCount !== undefined ? { ghostRowCount } : {}),
    defaultRowHeightPx:
      typeof o.defaultRowHeightPx === 'number' && o.defaultRowHeightPx > 0 ? o.defaultRowHeightPx : undefined,
    ...(normalizeCellOverflow(o.cellOverflow) !== undefined
      ? { cellOverflow: normalizeCellOverflow(o.cellOverflow) }
      : {}),
    enabledUiCapabilities,
    ...(chromeActions !== undefined ? { chromeActions } : {}),
    ...(sparseCells !== undefined ? { cells: sparseCells } : {}),
    ...(cellStyles !== undefined ? { cellStyles } : {}),
    ...(cellSpans.length > 0 ? { cellSpans } : {}),
    ...(floatingImages.length > 0 ? { floatingImages } : {}),
    ...(o.pdfExport === false ? { pdfExport: false } : {}),
  };
  return { sheetPath, payload };
}
